import { useState, useContext } from 'react';
import { LoggerContext } from "context/LoggerContext";
import { validateCheckoutForm } from '../checkoutHelpers';

/**
 * Hook for managing checkout form state and validation
 * @param {Object} initialData - Initial form data
 * @returns {Object} Form management methods and state
 */
const useCheckoutForm = (initialData = {}) => {
	const { logDebug } = useContext(LoggerContext);
	const [formData, setFormData] = useState(initialData);
	const [formErrors, setFormErrors] = useState({});

	/**
	 * Formats order data for API submission
	 * @returns {Object} - Formatted order data object
	 */
	const formatOrderDataForApi = (activeCoupon, expandedTickets, apiItems, items) => {
		let firstName = formData.name;
		let lastName = formData.lastName;
		if (firstName && !lastName) {
			const parts = firstName.split(" ");
			if (parts.length > 1) {
				lastName = parts.pop();
				firstName = parts.join(" ");
			}
		}

		const jsonData = {
			item: { article: [] },
			buyer: {
				form_of_address: formData.formOfAddress || "Herr",
				title: formData.title || "",
				name: firstName,
				last_name: lastName,
				email: formData.email,
				phone: formData.phone || "",
				po_number: formData.poNumber || ""
			},
			invoice_address: {
				invoicePostalAddress: {
					streetAddress: formData.street,
					addressSuffix: formData.addressSuffix || "",
					postalCode: formData.zipCode,
					city: formData.city,
					country: formData.country === "DE"
						? "Deutschland"
						: formData.country === "AT"
							? "Österreich"
							: formData.country === "CH"
								? "Schweiz"
								: "Deutschland"
				},
				invoiceEmailAddress: formData.email
			},
			invitations: [],
			payment: formData.paymentMethod || ""
		};

		// Add company information if applicable
		if (formData.isCompany) {
			jsonData.buyer.company_name = formData.companyName;
			jsonData.buyer.company_position = formData.companyPosition || "";
			jsonData.buyer.company_position_id = formData.companyPositionId || "";
			jsonData.buyer.company_industry = formData.companyIndustry || "";
			jsonData.buyer.company_industry_id = formData.companyIndustryId || "";
			jsonData.buyer.company_size = formData.companySize || "";
			jsonData.buyer.company_size_id = formData.companySizeId || "";

			// Format VAT ID with prefix for API
			let apiVatId = formData.vatId;
			if (formData.vatCountry === "DE" && !apiVatId.startsWith("DE")) apiVatId = `DE${apiVatId}`;
			else if (formData.vatCountry === "AT" && !apiVatId.startsWith("AT")) apiVatId = `AT${apiVatId}`;
			else if (formData.vatCountry === "CH" && !apiVatId.startsWith("CHE")) apiVatId = `CHE${apiVatId}`;
			jsonData.buyer.vat_number = apiVatId;
		}

		if (activeCoupon?.code) jsonData.coupon = { code: activeCoupon.code };

		if (apiItems.length > 0) {
			apiItems.forEach(item => {
				if (item.originalArticle) {
					const articleCopy = { ...item.originalArticle };
					// Ensure count is a string
					if (articleCopy.count !== undefined) {
						articleCopy.count = String(articleCopy.count);
					}
					jsonData.item.article.push(articleCopy);
				}
			});
		} else if (items.length > 0) {
			items.forEach(item => {
				jsonData.item.article.push({
					id: item.id,
					count: String(item.quantity || 1), // Convert count to string
					type_variant_id: 0,
					instance: {
						id: item.id,
						title: item.product_name,
						desc: item.description || "",
						cost: { sale_price: item.price }
					},
					article_price: {
						currency: "Euro",
						total_net_amount: (item.price * (item.quantity || 1) / 1.19).toFixed(2),
						total_gross_amount: (item.price * (item.quantity || 1)).toFixed(2),
						total_tax_amount: (item.price * (item.quantity || 1) * 0.19 / 1.19).toFixed(2)
					}
				});
			});
		}

		// Add invitations for event tickets if available
		if (expandedTickets.length > 0) {
			expandedTickets.forEach(ticket => {
				jsonData.invitations.push({
					article_id: ticket.baseItemId,
					is_for_orderer: ticket.isForOrderer ? 1 : 0,
					email: ticket.isForOrderer ? formData.email : ticket.invitee.email,
					first_name: ticket.isForOrderer ? firstName : ticket.invitee.firstName,
					last_name: ticket.isForOrderer ? lastName : ticket.invitee.lastName,
					privacy: 1,
					preferences: ticket.preferences || [],
					extra_info: { ticket_number: ticket.ticketNumber }
				});
			});
		}

		return jsonData;
	};

	/**
	 * Handles form input changes and updates corresponding state
	 * @param {Event} e - Input change event
	 */
	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
		if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
		
		return { name, value, type, checked };
	};

	/**
	 * Clears a specific error from the form errors state
	 * @param {string} errorKey - The key of the error to clear
	 */
	const clearError = (errorKey) => {
		setFormErrors(prev => {
			const newErrors = { ...prev };
			delete newErrors[errorKey];
			return newErrors;
		});
	};

	/**
	 * Validates the checkout form
	 * @param {Array} expandedTickets - Ticket data for validation
	 * @returns {boolean} - Whether the form is valid
	 */
	const validateForm = (expandedTickets) => {
		const errors = validateCheckoutForm(formData, expandedTickets);
		setFormErrors(errors);
		
		if (Object.keys(errors).length > 0) {
			logDebug("Form validation failed", errors);

			// Find first error and scroll to it
			const firstErrorKey = Object.keys(errors)[0];
			const element = document.querySelector(`[data-field="${firstErrorKey}"]`);

			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });
				element.focus();
			}
			
			return false;
		}
		
		return true;
	};

	return {
		formData,
		setFormData,
		formErrors,
		setFormErrors,
		handleInputChange,
		clearError,
		validateForm,
		formatOrderDataForApi
	};
};

export default useCheckoutForm;
